import React, { useEffect, useRef, useState } from "react";
import { Loader2, AlertCircle, Anchor } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { runOAuthSessionExchange } from "@/lib/mobile/authOrchestration";
import { useAuth } from "@/context/AuthContext";

// PD-028 · Emergent Google Auth callback landing.
// Detects the `#session_id=…` fragment set by Emergent after the user
// completes Google sign-in, exchanges it with the backend for a
// server-side session, then hands control back to the app.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const { refreshMe } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      // Parse `session_id` out of the URL fragment.
      const hash = window.location.hash || "";
      const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
      const sessionId = params.get("session_id");
      if (!sessionId) {
        setError("Missing session_id. Please try signing in again.");
        return;
      }
      // Strip the fragment immediately so a refresh doesn't re-post.
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState({}, "", cleanUrl);

      try {
        // RC-MOBILE-AUTH-REPAIR-001 Stage 1c · Split web vs native
        // callback exchange. Web keeps the existing cookie-based
        // /api/auth/google/session path. Native uses the mobile
        // bearer-token endpoint (no cookies) so the session survives
        // the WebView's cross-origin cookie constraints.
        await runOAuthSessionExchange(sessionId);
        // M3 · Restore any preserved post-login hash (e.g. an invitation
        // review URL the anonymous user first tapped) BEFORE refreshMe
        // so that <CruiseProvider>'s initial route reader picks it up.
        try {
          const saved = sessionStorage.getItem("cc_post_login_hash");
          if (saved) {
            sessionStorage.removeItem("cc_post_login_hash");
            // history.replaceState avoids adding a spurious entry.
            window.history.replaceState(
              {},
              "",
              window.location.pathname + window.location.search + saved,
            );
          }
        } catch { /* sessionStorage unavailable — silently ignore */ }
        // Pull /api/auth/me and let AuthGate flip us into the Shell.
        const me = await refreshMe();
        if (!me) {
          // Exchange succeeded but /me never hydrated (refreshMe swallows 401s
          // and returns null). Never leave the traveller on an infinite spinner.
          setError("Signed in with Google, but the session could not be established. Please try again.");
        }
      } catch (err) {
        setError(formatApiError(err));
      }
    })();
  }, [refreshMe]);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-[#0F172A] text-white"
      data-testid="cc-auth-callback"
    >
      <div className="max-w-sm text-center px-8">
        <div className="w-11 h-11 mx-auto mb-6 rounded-xl bg-white/10 border border-white/20 backdrop-blur flex items-center justify-center">
          <Anchor className="w-5 h-5 text-white" strokeWidth={2.2} />
        </div>
        {!error ? (
          <>
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-4 text-white/80" />
            <p className="text-[13.5px] tracking-[0.16em] uppercase font-semibold text-white/70">
              Signing you in…
            </p>
          </>
        ) : (
          <>
            <AlertCircle className="w-6 h-6 mx-auto mb-4 text-red-400" />
            <p role="alert" className="text-[14px] text-white/80 leading-relaxed mb-6" data-testid="cc-auth-callback-error">
              {error}
            </p>
            <a
              href="/"
              data-testid="cc-auth-callback-retry"
              className="inline-flex items-center gap-2 rounded-full px-5 h-11 text-[13px] font-bold text-[#1a1204]"
              style={{ background: "linear-gradient(135deg, #ffd66b 0%, #f5b400 100%)" }}
            >
              Back to sign in
            </a>
          </>
        )}
      </div>
    </div>
  );
}
